// Renderer-side bridge to the Electron shell (packages/desktop). No-ops in the
// browser. The shell exposes a minimal, allow-listed API via its preload
// (window.brizinaDesktop); this module adapts it to the app.

import { Emitter } from '@brizina/call-engine';

export interface ShareSource {
  id: string;
  name: string;
  thumbnail: string;
}

interface CallNotify {
  title?: string;
  body?: string;
  callId?: string;
}

interface DesktopAPI {
  isDesktop: boolean;
  onPushToTalk(handler: () => void): () => void;
  /** Raise a native OS notification for an incoming call (Layer A bridge) —
   * fires even with the window hidden / the app sitting in the tray. */
  notifyIncomingCall?(payload: CallNotify): void;
  /** Notification click / brizina://call/<id> deep link. */
  onOpenCall?(handler: (callId: string) => void): () => void;
}

declare global {
  interface Window {
    brizinaDesktop?: DesktopAPI;
    /** Called by the Electron main process to run our custom share picker. */
    __bzPickShareSource?: (sources: ShareSource[]) => Promise<string | null>;
  }
}

export const isDesktop = (): boolean => !!window.brizinaDesktop?.isDesktop;

/** Emits when the main process asks us to pick a screen-share source. */
export const sharePickerEvents = new Emitter<{ request: [ShareSource[]] }>();
let resolvePick: ((id: string | null) => void) | null = null;

/** The renderer resolves the pending pick with the chosen source (or null). */
export function resolveSharePick(id: string | null): void {
  resolvePick?.(id);
  resolvePick = null;
}

// Install the picker hook the Electron main process invokes.
if (typeof window !== 'undefined' && isDesktop()) {
  window.__bzPickShareSource = (sources: ShareSource[]) =>
    new Promise<string | null>((resolve) => {
      resolvePick = resolve;
      sharePickerEvents.emit('request', sources);
    });
}

/** Subscribe to the global push-to-talk toggle (desktop only). */
export function onDesktopPushToTalk(handler: () => void): () => void {
  return window.brizinaDesktop?.onPushToTalk(handler) ?? (() => {});
}

/** Ask the desktop shell to raise a native incoming-call notification. No-op in
 * the browser — there the in-app sheet is the only surface. */
export function desktopNotifyCall(payload: CallNotify): void {
  window.brizinaDesktop?.notifyIncomingCall?.(payload);
}

/** Subscribe to "open this call" from a notification click or deep link. */
export function onDesktopOpenCall(handler: (callId: string) => void): () => void {
  return window.brizinaDesktop?.onOpenCall?.(handler) ?? (() => {});
}
